import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { cameraGroupsApi } from '@api/camera-groups';
import { HttpError } from '@api/http';
import type { CameraGroup } from '@api/types';

interface UseCameraGroupsResult {
  groups: CameraGroup[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useCameraGroups(): UseCameraGroupsResult {
  const [groups, setGroups] = useState<CameraGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reloadCount = useRef(0);

  const refresh = useCallback(() => {
    reloadCount.current += 1;
    setIsLoading(true);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    cameraGroupsApi
      .list({ pageSize: 100, sortBy: 'name', sortOrder: 'asc' }, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        setGroups(res.items);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        const message =
          err instanceof HttpError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load groups';
        setError(message);
        setIsLoading(false);
      });
    return () => controller.abort();
    // reloadCount.current intentionally drives refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadCount.current]);

  return { groups, isLoading, error, refresh };
}
